import { apiClient } from '@shared/api/client';

/**
 * Task Configuration 문서 타입 정의
 */
export interface TaskConfigDocument {
  DOC_TYPE: string;
  TMP_ID?: string | number;
  templateName?: string;
  isRequired: boolean;
  category: string | null;
  filter: string | null;  // FILTER 값 (Position 코드 등)
}

/**
 * Task Configuration 타입 정의
 */
export interface TaskConfig {
  taskConfigId: string;
  jurisdiction: string;
  taskConfigCd: string;  // L_BASECODE의 TOTAL_CODE (예: 'B32001')
  taskName: string;      // 다국어 처리된 이름
  taskNameEn: string;    // 다국어 처리된 이름 (영어)
  documents: TaskConfigDocument[] | null;
  processSteps: string[] | null;
  formComponent: string | null;  // Vue 컴포넌트 이름 (예: 'OfficerAppointmentForm')
  displayOrder: number;
  taskGroup: string | null;       // TASK_GROUP 코드 (예: 'B39001')
  taskGroupDesc: string | null;   // TASK_GROUP_DESC 코드 (예: 'B40001')
  taskGroupName: string | null;   // TASK_GROUP 다국어 이름 (L_BASECODE에서 조회)
  taskGroupDescText: string | null; // TASK_GROUP_DESC 다국어 텍스트 (L_BASECODE에서 조회)
  isHidden?: boolean; // L_TASK_CONFIG.IS_DEL (Show/Hidden). true면 카드 그리드에서 제외 (T-Doc AI 조회는 허용)
}

/**
 * Task Configuration 목록 조회
 * 세션의 customer.jurisdiction을 사용하여 조회
 */
export const getTaskConfigs = async (): Promise<TaskConfig[]> => {
  const response = await apiClient.get<{ taskConfigs: TaskConfig[] }>('/startTask/task-config');
  // apiClient.get()은 ApiResponse<T>를 반환하므로 response.data로 접근
  return response.data?.taskConfigs || [];
};

/**
 * 특정 Task Configuration 조회
 * taskConfigId로 단일 레코드 조회
 */
export const getTaskConfigById = async (taskConfigId: string): Promise<TaskConfig | null> => {
  const response = await apiClient.get<{ taskConfig: TaskConfig | null }>(`/startTask/task-config/${taskConfigId}`);
  return response.data?.taskConfig || null;
};

/**
 * TASK_CONFIG_CD (예: 'M32100') 로 단일 Task Configuration 조회
 * - IS_DEL = 0 필터 적용 (삭제 레코드 제외)
 * - JURISDICTION 제한 없음
 */
export const getTaskConfigByCode = async (taskConfigCd: string): Promise<TaskConfig | null> => {
  const code = encodeURIComponent(taskConfigCd);
  const response = await apiClient.get<{ taskConfig: TaskConfig | null }>(`/startTask/task-config/by-code/${code}`);
  return response.data?.taskConfig || null;
};

/**
 * Filter 값으로 문서 목록 조회
 * @param taskConfigId - Task Config ID
 * @param filterValue - filter 값 (예: Position 코드). null이면 filter가 없는 문서만 조회
 */
export const getDocumentsByFilter = async (
  taskConfigId: string,
  filterValue: string | null = null
): Promise<{ documents: TaskConfigDocument[]; filterValue: string | null }> => {
  const params = filterValue ? { filterValue } : {};
  const response = await apiClient.get<{
    documents: TaskConfigDocument[];
    filterValue: string | null;
  }>(`/startTask/task-config/${taskConfigId}/documents-by-filter`, { params });
  return {
    documents: response.data?.documents || [],
    filterValue: response.data?.filterValue || null
  };
};

